import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  useModelIndex,
  useHasRoleIn,
  useModelInvalidateIndex
} from '@rhino-project/core/hooks';
import { networkApiCallOnlyData } from '@rhino-project/core/lib';
import { formatDate, formatMoney, statusBadgeClass } from '../lib/format';
import type { ReservationRow } from '../lib/harborlightTypes';

// Ported 1:1 from the source repository (client/src/pages/ReservationsPage.jsx).
// Data via Rhino `reservation` model index; lifecycle actions via the custom
// /api/reservations/:id/{check-in,check-out,cancel} endpoints.
const ReservationsPage = () => {
  const { owner } = useParams({ strict: false }) as { owner: string };
  const isStaff = useHasRoleIn(['staff', 'admin']);
  const { resources } = useModelIndex('reservation', { limit: 200 });
  const invalidate = useModelInvalidateIndex('reservation');
  const reservations = resources?.results ?? [];

  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');

  async function runAction(
    id: number | string,
    action: 'check-in' | 'check-out' | 'cancel'
  ) {
    setBusyId(String(id) + action);
    setError('');
    try {
      await networkApiCallOnlyData(`/api/reservations/${id}/${action}`, {
        method: 'post'
      });
      invalidate.invalidate();
    } catch {
      setError(`Could not ${action.replace('-', ' ')} this reservation.`);
    } finally {
      setBusyId('');
    }
  }

  return (
    <div className="fade-up">
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-4">
        <div>
          <p className="text-uppercase small text-secondary mb-1">Stays</p>
          <h1 className="display-title display-5 mb-0">
            {isStaff ? 'Reservations' : 'My bookings'}
          </h1>
        </div>
        <Link
          className="btn btn-outline-sea"
          to="/$owner/rooms"
          params={{ owner }}
        >
          {isStaff ? 'Create from rooms' : 'Browse rooms'}
        </Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="surface-panel p-3 p-md-4">
        {reservations.length === 0 ? (
          <p className="text-secondary mb-0 p-2">No reservations yet.</p>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  {isStaff && <th>Guest</th>}
                  <th>Room</th>
                  <th>Dates</th>
                  <th>Guests</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((item: ReservationRow) => (
                  <tr key={item.id}>
                    {isStaff && (
                      <td>{item.user?.display_name ?? item.user?.name}</td>
                    )}
                    <td>
                      #{item.room?.number}
                      <div className="small text-secondary">
                        {item.room?.room_type}
                      </div>
                    </td>
                    <td>
                      {formatDate(item.check_in)} → {formatDate(item.check_out)}
                    </td>
                    <td>{item.guests}</td>
                    <td>{formatMoney(item.total_price)}</td>
                    <td>
                      <span className={statusBadgeClass(item.status)}>
                        {item.status}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-1">
                        {isStaff &&
                          ['CONFIRMED', 'PENDING'].includes(item.status) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-sea"
                              disabled={busyId === String(item.id) + 'check-in'}
                              onClick={() => runAction(item.id, 'check-in')}
                            >
                              Check in
                            </button>
                          )}
                        {isStaff && item.status === 'CHECKED_IN' && (
                          <button
                            type="button"
                            className="btn btn-sm btn-copper"
                            disabled={busyId === String(item.id) + 'check-out'}
                            onClick={() => runAction(item.id, 'check-out')}
                          >
                            Check out
                          </button>
                        )}
                        {['CONFIRMED', 'PENDING'].includes(item.status) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={busyId === String(item.id) + 'cancel'}
                            onClick={() => runAction(item.id, 'cancel')}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationsPage;
